import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';

const TermsOfService = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="terms-page">
      <SEO 
        title="Terms of Service"
        description="Read the terms and conditions for using Branding Yard's website and services."
        url="/terms"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Terms of Service' }
        ]}
      />
      
      {/* Header */}
      <section className="bg-[#0A0A0A] py-16 md:py-24">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 lg:px-24">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-sm text-[#737373] hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <h1 className="font-serif text-3xl md:text-5xl font-semibold tracking-tight text-white">
            Terms of Service
          </h1>
          <p className="mt-4 text-[#A3A3A3]">
            Last updated: February 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl prose prose-lg">
            
            <div className="mb-12">
              <h2 className="font-serif text-2xl font-semibold text-[#0A0A0A] mb-4">Agreement to Terms</h2>
              <p className="text-[#737373] leading-relaxed">
                These Terms of Service constitute a legally binding agreement made between you, whether personally or on behalf of an entity ("you") and Branding Yard ("we," "us" or "our"), concerning your access to and use of the brandingyard.co.zw website as well as any other media form, media channel, mobile website or mobile application related, linked, or otherwise connected thereto (collectively, the "Site").
              </p>
              <p className="text-[#737373] leading-relaxed mt-4">
                You agree that by accessing the Site, you have read, understood, and agree to be bound by all of these Terms of Service. If you do not agree with all of these Terms of Service, then you are expressly prohibited from using the Site and you must discontinue use immediately.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="font-serif text-2xl font-semibold text-[#0A0A0A] mb-4">Products and Services</h2>
              <p className="text-[#737373] leading-relaxed">
                Branding Yard specializes in premium corporate wear, branded merchandise, and corporate gifting solutions. All products displayed on our website are subject to availability. We reserve the right to discontinue any product at any time without notice.
              </p>
              <h3 className="font-semibold text-[#0A0A0A] mt-6 mb-2">Product Descriptions</h3>
              <p className="text-[#737373] leading-relaxed">
                We make every effort to display as accurately as possible the colors and images of our products. We cannot guarantee that your computer monitor's display of any color will be accurate. We reserve the right to limit the quantities of any products or services that we offer.
              </p>
              <h3 className="font-semibold text-[#0A0A0A] mt-6 mb-2">Custom Branding</h3>
              <p className="text-[#737373] leading-relaxed">
                For custom branded products, you are responsible for ensuring that any logos, artwork, or designs you submit do not infringe on any third-party intellectual property rights. We reserve the right to refuse any order that we believe may violate trademark or copyright laws.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="font-serif text-2xl font-semibold text-[#0A0A0A] mb-4">Orders and Payments</h2>
              <h3 className="font-semibold text-[#0A0A0A] mt-6 mb-2">Order Acceptance</h3>
              <p className="text-[#737373] leading-relaxed">
                All orders placed through the Site are subject to our acceptance. We reserve the right to refuse or cancel any order for any reason, including but not limited to product availability, errors in product or pricing information, or problems identified by our fraud detection systems.
              </p>
              <h3 className="font-semibold text-[#0A0A0A] mt-6 mb-2">Pricing</h3>
              <p className="text-[#737373] leading-relaxed">
                All prices are displayed in USD unless otherwise stated. We reserve the right to change prices at any time without notice. However, any price changes will not affect orders that have already been placed and confirmed.
              </p>
              <h3 className="font-semibold text-[#0A0A0A] mt-6 mb-2">Payment Terms</h3>
              <p className="text-[#737373] leading-relaxed">
                We accept various forms of payment as indicated on the Site. You agree to provide current, complete, and accurate purchase and account information for all purchases made on our Site. You further agree to promptly update account and payment information to complete your transactions and contact us as needed.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="font-serif text-2xl font-semibold text-[#0A0A0A] mb-4">Shipping and Delivery</h2>
              <p className="text-[#737373] leading-relaxed">
                We ship products within Zimbabwe and to select international destinations. Shipping costs and delivery times vary depending on the destination and shipping method selected. Estimated delivery times are provided for convenience only and are not guaranteed.
              </p>
              <p className="text-[#737373] leading-relaxed mt-4">
                Risk of loss and title for all products pass to you upon delivery to the carrier. We are not responsible for delays caused by the shipping carrier, customs clearance, or other factors beyond our control.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="font-serif text-2xl font-semibold text-[#0A0A0A] mb-4">Returns and Refunds</h2>
              <p className="text-[#737373] leading-relaxed mb-4">
                We want you to be completely satisfied with your purchase. Our return policy is as follows:
              </p>
              <ul className="list-disc list-inside text-[#737373] space-y-2">
                <li>Standard products may be returned within 14 days of delivery if unused and in original packaging</li>
                <li>Custom branded products cannot be returned unless there is a defect in the product or branding</li>
                <li>Refunds will be processed within 7-10 business days of receiving the returned item</li>
                <li>Shipping costs for returns are the responsibility of the customer unless the return is due to our error</li>
              </ul>
              <p className="text-[#737373] leading-relaxed mt-4">
                To initiate a return, please contact our customer service team with your order number and reason for return.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="font-serif text-2xl font-semibold text-[#0A0A0A] mb-4">Intellectual Property</h2>
              <p className="text-[#737373] leading-relaxed">
                Unless otherwise indicated, the Site is our proprietary property and all source code, databases, functionality, software, website designs, audio, video, text, photographs, and graphics on the Site (collectively, the "Content") and the trademarks, service marks, and logos contained therein (the "Marks") are owned or controlled by us or licensed to us, and are protected by copyright and trademark laws.
              </p>
              <p className="text-[#737373] leading-relaxed mt-4">
                The Content and the Marks are provided on the Site "AS IS" for your information and personal use only. Except as expressly provided in these Terms of Service, no part of the Site and no Content or Marks may be copied, reproduced, aggregated, republished, uploaded, posted, publicly displayed, encoded, translated, transmitted, distributed, sold, licensed, or otherwise exploited for any commercial purpose whatsoever, without our express prior written permission.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="font-serif text-2xl font-semibold text-[#0A0A0A] mb-4">User Representations</h2>
              <p className="text-[#737373] leading-relaxed mb-4">
                By using the Site, you represent and warrant that:
              </p>
              <ul className="list-disc list-inside text-[#737373] space-y-2">
                <li>All registration information you submit will be true, accurate, current, and complete</li>
                <li>You will maintain the accuracy of such information and promptly update as necessary</li>
                <li>You have the legal capacity and you agree to comply with these Terms of Service</li>
                <li>You are not a minor in the jurisdiction in which you reside</li>
                <li>You will not access the Site through automated or non-human means</li>
                <li>You will not use the Site for any illegal or unauthorized purpose</li>
                <li>Your use of the Site will not violate any applicable law or regulation</li>
              </ul>
            </div>

            <div className="mb-12">
              <h2 className="font-serif text-2xl font-semibold text-[#0A0A0A] mb-4">Prohibited Activities</h2>
              <p className="text-[#737373] leading-relaxed mb-4">
                You may not access or use the Site for any purpose other than that for which we make the Site available. The Site may not be used in connection with any commercial endeavors except those that are specifically endorsed or approved by us. As a user of the Site, you agree not to:
              </p>
              <ul className="list-disc list-inside text-[#737373] space-y-2">
                <li>Systematically retrieve data or other content from the Site to create a collection, compilation, database, or directory</li>
                <li>Make any unauthorized use of the Site</li>
                <li>Circumvent, disable, or otherwise interfere with security-related features of the Site</li>
                <li>Engage in unauthorized framing of or linking to the Site</li>
                <li>Trick, defraud, or mislead us and other users</li>
                <li>Interfere with, disrupt, or create an undue burden on the Site or its networks</li>
                <li>Attempt to impersonate another user or person</li>
                <li>Use any information obtained from the Site to harass, abuse, or harm another person</li>
              </ul>
            </div>

            <div className="mb-12">
              <h2 className="font-serif text-2xl font-semibold text-[#0A0A0A] mb-4">Limitation of Liability</h2>
              <p className="text-[#737373] leading-relaxed">
                In no event will we or our directors, employees, or agents be liable to you or any third party for any direct, indirect, consequential, exemplary, incidental, special, or punitive damages, including lost profit, lost revenue, loss of data, or other damages arising from your use of the Site, even if we have been advised of the possibility of such damages.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="font-serif text-2xl font-semibold text-[#0A0A0A] mb-4">Indemnification</h2>
              <p className="text-[#737373] leading-relaxed">
                You agree to defend, indemnify, and hold us harmless, including our subsidiaries, affiliates, and all of our respective officers, agents, partners, and employees, from and against any loss, damage, liability, claim, or demand, including reasonable attorneys' fees and expenses, made by any third party due to or arising out of your use of the Site, your breach of these Terms of Service, or your violation of the rights of a third party.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="font-serif text-2xl font-semibold text-[#0A0A0A] mb-4">Governing Law</h2>
              <p className="text-[#737373] leading-relaxed">
                These Terms of Service and your use of the Site are governed by and construed in accordance with the laws of Zimbabwe applicable to agreements made and to be entirely performed within Zimbabwe, without regard to its conflict of law principles.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="font-serif text-2xl font-semibold text-[#0A0A0A] mb-4">Changes to Terms</h2>
              <p className="text-[#737373] leading-relaxed">
                We reserve the right, in our sole discretion, to make changes or modifications to these Terms of Service at any time and for any reason. We will alert you about any changes by updating the "Last updated" date of these Terms of Service, and you waive any right to receive specific notice of each such change.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="font-serif text-2xl font-semibold text-[#0A0A0A] mb-4">Contact Us</h2>
              <p className="text-[#737373] leading-relaxed">
                If you have questions or comments about these Terms of Service, please contact us at:
              </p>
              <div className="mt-4 p-6 bg-[#FAFAFA] border border-[#E5E5E5]">
                <p className="font-semibold text-[#0A0A0A]">Branding Yard</p>
                <p className="text-[#737373] mt-2">Email: info@brandingyard.co.zw</p>
                <p className="text-[#737373]">Harare, Zimbabwe</p>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;
